/**
 * MinistrareIA - Leads Page
 * Painel de manutenção dos clientes do Desafio: quem pagou, em que dia está, quem está parado,
 * o que falhou — com ações de manutenção por lead e em lote (25/09/2026).
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Users, Search, Plus, Edit2, Trash2, X, Send,
  RotateCcw, Loader2, AlertCircle, Check,
  Clock, AlertTriangle,
  MoreHorizontal, MessageSquare,
  Bell, CalendarCheck, Eye, RefreshCw, UserPlus, UserMinus, Package,
  CheckCircle2, XCircle, PauseCircle, Flag, CheckSquare, Square, ShoppingCart, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import leadService from '../../services/global/leadService';
import productService from '../../services/global/productService';
import followupService from '../../services/global/followupService';
import RecipientDetailModal from '../../components/followups/RecipientDetailModal';


// Situação calculada pelo backend (services/leads_painel.py) → rótulo/cor
export const SITUACAO_LEAD = {
  em_dia: { label: 'Em dia', color: '#22c55e' },
  em_espera: { label: 'Parado (sem resposta)', color: '#f59e0b' },
  pendente: { label: 'Reenvio pendente', color: '#3b82f6' },
  falha: { label: 'Falha de envio', color: '#ef4444' },
  concluido: { label: 'Concluído', color: '#64748b' },
  aguardando_inicio: { label: 'Começa em breve', color: '#64748b' },
  sem_data: { label: 'Sem data', color: '#64748b' },
  nao_inscrito: { label: 'Pagou e NÃO está inscrito', color: '#ef4444' },
  sem_telefone: { label: 'Pagou sem telefone', color: '#ef4444' },
  sem_followup: { label: 'Produto sem follow-up', color: '#f59e0b' },
  nao_pago: { label: 'Não pago', color: '#64748b' },
};

// ============================================================
// STATUS BADGE
// ============================================================

const StatusBadge = ({ status, colors }) => {
  const config = {
    pending: { label: 'Pendente', bg: '#f59e0b20', color: '#f59e0b' },
    active: { label: 'Ativo', bg: '#22c55e20', color: '#22c55e' },
    overdue: { label: 'Inadimplente', bg: '#ef444420', color: '#ef4444' },
    refunded: { label: 'Estornado', bg: '#8b5cf620', color: '#8b5cf6' },
  };
  const c = config[status] || config.pending;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
      fontWeight: 600, backgroundColor: c.bg, color: c.color,
    }}>
      {c.label}
    </span>
  );
};


// ============================================================
// CONFIRM MODAL
// ============================================================

const ConfirmModal = ({ title, message, onConfirm, onCancel, loading, colors }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 2000,
  }} onClick={onCancel}>
    <div style={{
      backgroundColor: colors.bgPrimary, borderRadius: '12px',
      padding: '24px', maxWidth: '400px', width: '90%',
      border: `1px solid ${colors.borderColor}`,
    }} onClick={e => e.stopPropagation()}>
      <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>{title}</h3>
      <p style={{ margin: '0 0 20px', fontSize: '14px', color: colors.textSecondary }}>{message}</p>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} disabled={loading} style={{
          padding: '8px 16px', border: `1px solid ${colors.borderColor}`,
          borderRadius: '8px', backgroundColor: 'transparent',
          color: colors.textPrimary, cursor: 'pointer', fontSize: '13px',
        }}>Cancelar</button>
        <button onClick={onConfirm} disabled={loading} style={{
          padding: '8px 16px', border: 'none', borderRadius: '8px',
          backgroundColor: '#ef4444', color: 'white', cursor: 'pointer',
          fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          {loading && <Loader2 size={14} className="spin" />}
          Confirmar
        </button>
      </div>
    </div>
  </div>
);


// ============================================================
// SEND MESSAGE MODAL
// ============================================================

const SendMessageModal = ({ lead, onClose, onSend, colors }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSend = async () => {
    if (!message.trim()) return;
    setLoading(true);
    setError('');
    try {
      await onSend(lead.id, message);
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onClose}>
      <div style={{
        backgroundColor: colors.bgPrimary, borderRadius: '12px',
        padding: '24px', maxWidth: '480px', width: '90%',
        border: `1px solid ${colors.borderColor}`,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            Enviar mensagem para {lead.name}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary }}>
            <X size={18} />
          </button>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: '13px', color: colors.textSecondary }}>
          Telefone: {lead.phone || 'Nao informado'}
        </p>

        {success ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px', color: '#22c55e' }}>
            <Check size={18} /> Mensagem enviada!
          </div>
        ) : (
          <>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Digite a mensagem..."
              rows={4}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
                color: colors.textPrimary, fontSize: '14px', resize: 'vertical',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
            {error && (
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#ef4444' }}>{error}</p>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={onClose} style={{
                padding: '8px 16px', border: `1px solid ${colors.borderColor}`,
                borderRadius: '8px', backgroundColor: 'transparent',
                color: colors.textPrimary, cursor: 'pointer', fontSize: '13px',
              }}>Cancelar</button>
              <button onClick={handleSend} disabled={loading || !message.trim()} style={{
                padding: '8px 16px', border: 'none', borderRadius: '8px',
                backgroundColor: colors.accentPrimary, color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px',
                display: 'flex', alignItems: 'center', gap: '6px',
                opacity: loading || !message.trim() ? 0.6 : 1,
              }}>
                {loading ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                Enviar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


// ============================================================
// EDIT / CREATE LEAD MODAL (with product + followup selectors)
// ============================================================

const EditLeadModal = ({ lead, onClose, onSave, colors }) => {
  const isNew = !lead?.id;
  const [form, setForm] = useState({
    name: lead?.name || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    cpf_cnpj: lead?.cpf_cnpj || '',
    payment_link_id: lead?.payment_link_id || '',
    plan_status: lead?.plan_status || 'active',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Product + followup lists
  const [products, setProducts] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoadingOptions(true);
      try {
        const [prodRes, fuRes] = await Promise.all([
          productService.list(),
          followupService.list(),
        ]);
        const prodList = prodRes?.data || prodRes || [];
        const fuList = fuRes?.data || fuRes || [];
        setProducts(Array.isArray(prodList) ? prodList : []);
        setFollowups(Array.isArray(fuList) ? fuList : []);
      } catch (err) {
        console.error('Erro ao carregar opcoes:', err);
      } finally {
        setLoadingOptions(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Nome e obrigatorio');
      return;
    }
    if (!form.phone.trim()) {
      setError('Telefone e obrigatorio');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onSave(lead?.id, form);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
    color: colors.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'auto',
    cursor: 'pointer',
  };

  const labelStyle = {
    fontSize: '12px', fontWeight: 500, color: colors.textSecondary,
    marginBottom: '4px', display: 'block',
  };

  // Get product's linked followup
  const selectedProduct = products.find(p => p.id === form.payment_link_id || p.payment_link_id === form.payment_link_id);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onClose}>
      <div style={{
        backgroundColor: colors.bgPrimary, borderRadius: '12px',
        padding: '24px', maxWidth: '500px', width: '90%',
        border: `1px solid ${colors.borderColor}`,
        maxHeight: '90vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            {isNew ? 'Novo Lead' : 'Editar Lead'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Name */}
          <div>
            <label style={labelStyle}>Nome *</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} placeholder="Nome do lead" />
          </div>

          {/* Phone + Email row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Telefone *</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} placeholder="5511999998888" />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} placeholder="email@exemplo.com" />
            </div>
          </div>

          {/* CPF + Status row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>CPF/CNPJ</label>
              <input value={form.cpf_cnpj} onChange={e => setForm({ ...form, cpf_cnpj: e.target.value })} style={inputStyle} placeholder="000.000.000-00" />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select
                value={form.plan_status}
                onChange={e => setForm({ ...form, plan_status: e.target.value })}
                style={selectStyle}
              >
                <option value="active">Ativo</option>
                <option value="pending">Pendente</option>
                <option value="overdue">Inadimplente</option>
                <option value="refunded">Estornado</option>
              </select>
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: '1px', backgroundColor: colors.borderColor, margin: '4px 0' }} />

          {/* Product Selector */}
          <div>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Package size={12} /> Produto
            </label>
            {loadingOptions ? (
              <div style={{ padding: '10px', fontSize: '13px', color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Loader2 size={14} className="spin" /> Carregando produtos...
              </div>
            ) : (
              <select
                value={form.payment_link_id}
                onChange={e => setForm({ ...form, payment_link_id: e.target.value })}
                style={selectStyle}
              >
                <option value="">Nenhum produto</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name || p.product_name || p.id}
                  </option>
                ))}
              </select>
            )}
            {selectedProduct && (
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: colors.textTertiary }}>
                {selectedProduct.followup_id
                  ? `Follow-up vinculado: ${followups.find(f => f.id === selectedProduct.followup_id)?.name || selectedProduct.followup_id}`
                  : 'Nenhum follow-up vinculado a este produto'}
              </p>
            )}
          </div>
        </div>

        {error && <p style={{ margin: '14px 0 0', fontSize: '13px', color: '#ef4444' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', border: `1px solid ${colors.borderColor}`,
            borderRadius: '8px', backgroundColor: 'transparent',
            color: colors.textPrimary, cursor: 'pointer', fontSize: '13px',
          }}>Cancelar</button>
          <button onClick={handleSave} disabled={loading} style={{
            padding: '8px 16px', border: 'none', borderRadius: '8px',
            backgroundColor: colors.accentPrimary, color: 'white',
            cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            {loading && <Loader2 size={14} className="spin" />}
            {isNew ? 'Criar Lead' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// SITUAÇÃO (badge) + ALERTAS
// ============================================================

const SituacaoBadge = ({ situacao, colors }) => {
  const c = SITUACAO_LEAD[situacao] || { label: situacao || '-', color: colors.textSecondary };
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
      backgroundColor: `${c.color}20`, color: c.color, whiteSpace: 'nowrap',
    }}>
      {c.label}
    </span>
  );
};

const AlertasCell = ({ lead, colors }) => {
  const alertas = lead.alertas || [];
  if (!alertas.length) {
    return <span style={{ fontSize: '12px', color: colors.textSecondary }}>—</span>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {alertas.map((a, i) => (
        <span key={i} style={{
          fontSize: '11px', lineHeight: '15px',
          color: lead.precisa_atencao ? '#ef4444' : colors.textSecondary,
          display: 'flex', alignItems: 'flex-start', gap: '4px',
        }}>
          <AlertTriangle size={11} style={{ marginTop: 2, flexShrink: 0 }} /> {a}
        </span>
      ))}
    </div>
  );
};


// ============================================================
// FILTRO CARD (cartão que também é filtro rápido)
// ============================================================

const FiltroCard = ({ label, value, icon: Icon, color, active, onClick, colors, hint }) => (
  <button
    onClick={onClick}
    title={hint}
    style={{
      flex: 1, minWidth: '130px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer',
      backgroundColor: active ? `${color}15` : colors.bgSecondary, borderRadius: '12px',
      border: `2px solid ${active ? color : colors.borderColor}`,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
      <span style={{ fontSize: '11px', color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
      <Icon size={15} color={color} />
    </div>
    <div style={{ fontSize: '24px', fontWeight: 700, color: active ? color : colors.textPrimary }}>{value}</div>
  </button>
);



// ============================================================
// FOLLOWUP DAYS MODAL
// ============================================================

const DAYS_PER_ROW = 10;

const FollowupDaysModal = ({ lead, onClose, onSaved, colors }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [followupData, setFollowupData] = useState(null);
  const [checkedDays, setCheckedDays] = useState(new Set());

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await leadService.getFollowupDays(lead.id);
        const data = res.data;
        if (!data) {
          setError(res.message || 'Este lead nao possui produto/follow-up vinculado.');
          setLoading(false);
          return;
        }
        setFollowupData(data);
        setCheckedDays(new Set(data.sent_days || []));
      } catch (err) {
        setError(err.response?.data?.detail || 'Erro ao carregar dados do follow-up');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [lead.id]);

  const toggleDay = (day) => {
    setCheckedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (!followupData) return;
    const all = new Set();
    for (let i = 1; i <= followupData.total_days; i++) all.add(i);
    setCheckedDays(all);
  };

  const deselectAll = () => {
    setCheckedDays(new Set());
  };

  const selectRange = (from, to) => {
    setCheckedDays(prev => {
      const next = new Set(prev);
      for (let i = from; i <= to; i++) next.add(i);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await leadService.updateFollowupDays(lead.id, Array.from(checkedDays));
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const totalDays = followupData?.total_days || 0;
  const allDays = Array.from({ length: totalDays }, (_, i) => i + 1);
  const originalSet = new Set(followupData?.sent_days || []);
  const hasChanges = (() => {
    if (checkedDays.size !== originalSet.size) return true;
    for (const d of checkedDays) if (!originalSet.has(d)) return true;
    return false;
  })();

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onClose}>
      <div style={{
        backgroundColor: colors.bgPrimary, borderRadius: '12px',
        padding: '24px', maxWidth: '640px', width: '95%',
        border: `1px solid ${colors.borderColor}`,
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
              Gerenciar dias do follow-up
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: colors.textSecondary }}>
              {lead.name} • {lead.phone}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary }}>
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>
            <Loader2 size={24} className="spin" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '13px' }}>Carregando...</div>
          </div>
        ) : error && !followupData ? (
          <div style={{ padding: '24px', textAlign: 'center', color: colors.textSecondary, fontSize: '14px' }}>
            <AlertCircle size={24} color="#f59e0b" style={{ marginBottom: '8px' }} />
            <div>{error}</div>
          </div>
        ) : followupData ? (
          <>
            {/* Followup info */}
            <div style={{
              padding: '10px 14px', borderRadius: '8px', marginBottom: '12px',
              backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderColor}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ fontSize: '13px', color: colors.textPrimary }}>
                <strong>{followupData.followup_name}</strong>
                <span style={{ color: colors.textSecondary, marginLeft: '8px' }}>
                  {totalDays} dias
                </span>
              </div>
              <div style={{ fontSize: '12px', color: colors.accentPrimary, fontWeight: 600 }}>
                {checkedDays.size}/{totalDays} recebidos
              </div>
            </div>

            {/* Bulk actions */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <button onClick={selectAll} style={{
                padding: '5px 10px', borderRadius: '6px', border: `1px solid ${colors.borderColor}`,
                background: 'transparent', color: colors.textSecondary, fontSize: '11px',
                cursor: 'pointer', fontWeight: 500,
              }}>
                Marcar todos
              </button>
              <button onClick={deselectAll} style={{
                padding: '5px 10px', borderRadius: '6px', border: `1px solid ${colors.borderColor}`,
                background: 'transparent', color: colors.textSecondary, fontSize: '11px',
                cursor: 'pointer', fontWeight: 500,
              }}>
                Desmarcar todos
              </button>
              {totalDays > 7 && (
                <>
                  <button onClick={() => selectRange(1, 7)} style={{
                    padding: '5px 10px', borderRadius: '6px', border: `1px solid ${colors.borderColor}`,
                    background: 'transparent', color: colors.textSecondary, fontSize: '11px',
                    cursor: 'pointer', fontWeight: 500,
                  }}>
                    Dias 1-7
                  </button>
                  {totalDays > 14 && (
                    <button onClick={() => selectRange(1, 14)} style={{
                      padding: '5px 10px', borderRadius: '6px', border: `1px solid ${colors.borderColor}`,
                      background: 'transparent', color: colors.textSecondary, fontSize: '11px',
                      cursor: 'pointer', fontWeight: 500,
                    }}>
                      Dias 1-14
                    </button>
                  )}
                  {totalDays > 21 && (
                    <button onClick={() => selectRange(1, 21)} style={{
                      padding: '5px 10px', borderRadius: '6px', border: `1px solid ${colors.borderColor}`,
                      background: 'transparent', color: colors.textSecondary, fontSize: '11px',
                      cursor: 'pointer', fontWeight: 500,
                    }}>
                      Dias 1-21
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Day grid */}
            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(DAYS_PER_ROW, totalDays)}, 1fr)`,
                gap: '6px',
              }}>
                {allDays.map(day => {
                  const isChecked = checkedDays.has(day);
                  const wasOriginal = originalSet.has(day);
                  const changed = isChecked !== wasOriginal;

                  return (
                    <div
                      key={day}
                      onClick={() => toggleDay(day)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', gap: '2px',
                        padding: '8px 4px', borderRadius: '8px', cursor: 'pointer',
                        backgroundColor: isChecked ? '#22c55e15' : colors.bgSecondary,
                        border: `2px solid ${changed ? '#f59e0b' : isChecked ? '#22c55e40' : colors.borderColor}`,
                        transition: 'all 0.1s',
                        minHeight: '48px',
                      }}
                    >
                      <div style={{
                        width: '18px', height: '18px', borderRadius: '4px',
                        border: `2px solid ${isChecked ? '#22c55e' : colors.borderColor}`,
                        backgroundColor: isChecked ? '#22c55e' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.1s',
                      }}>
                        {isChecked && <Check size={12} color="white" strokeWidth={3} />}
                      </div>
                      <span style={{
                        fontSize: '11px', fontWeight: 600,
                        color: isChecked ? '#22c55e' : colors.textSecondary,
                      }}>
                        {day}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div style={{
              display: 'flex', gap: '16px', marginTop: '12px',
              fontSize: '11px', color: colors.textTertiary,
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#22c55e', display: 'inline-block' }} />
                Recebido
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', border: '2px solid #f59e0b', display: 'inline-block' }} />
                Alterado
              </span>
            </div>

            {error && <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#ef4444' }}>{error}</p>}

            {/* Footer */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={onClose} style={{
                padding: '8px 16px', border: `1px solid ${colors.borderColor}`,
                borderRadius: '8px', backgroundColor: 'transparent',
                color: colors.textPrimary, cursor: 'pointer', fontSize: '13px',
              }}>Cancelar</button>
              <button onClick={handleSave} disabled={saving || !hasChanges} style={{
                padding: '8px 16px', border: 'none', borderRadius: '8px',
                backgroundColor: colors.accentPrimary, color: 'white',
                cursor: saving || !hasChanges ? 'not-allowed' : 'pointer', fontSize: '13px',
                display: 'flex', alignItems: 'center', gap: '6px',
                opacity: saving || !hasChanges ? 0.5 : 1,
              }}>
                {saving && <Loader2 size={14} className="spin" />}
                Salvar alteracoes
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};



// ============================================================
// REENVIAR DIA (escolher o dia) — 1 lead ou vários
// ============================================================

const ReenviarDiaModal = ({ leads, totalDays, onConfirm, onClose, colors }) => {
  const sugeridos = useMemo(() => {
    const s = new Set();
    leads.forEach(l => (l.participante?.falhas_dias || []).forEach(d => s.add(d)));
    leads.forEach(l => (l.participante?.pendentes_dias || []).forEach(d => s.add(d)));
    return s;
  }, [leads]);
  const [dia, setDia] = useState(sugeridos.size ? Math.min(...sugeridos) : 1);
  const [loading, setLoading] = useState(false);
  const total = totalDays || Math.max(21, ...leads.map(l => l.participante?.total_days || 0));
  const dias = Array.from({ length: total }, (_, i) => i + 1);

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={onClose}>
      <div style={{ backgroundColor: colors.bgPrimary, borderRadius: '12px', padding: '24px', maxWidth: '520px', width: '92%', border: `1px solid ${colors.borderColor}` }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
          Reenviar material de um dia
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: '13px', color: colors.textSecondary }}>
          Para {leads.length === 1 ? (leads[0].name || leads[0].phone) : `${leads.length} clientes`}. O dia da pessoa no desafio não muda.
          {sugeridos.size > 0 && <> Dias com falha/pendência estão destacados.</>}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '18px' }}>
          {dias.map(d => {
            const sel = d === dia;
            const sug = sugeridos.has(d);
            return (
              <button key={d} onClick={() => setDia(d)} style={{
                width: '40px', height: '34px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                border: `2px solid ${sel ? colors.accentPrimary : (sug ? '#ef4444' : colors.borderColor)}`,
                backgroundColor: sel ? `${colors.accentPrimary}20` : (sug ? '#ef444415' : 'transparent'),
                color: sel ? colors.accentPrimary : (sug ? '#ef4444' : colors.textPrimary),
              }}>{d}</button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={loading} style={{ padding: '8px 16px', border: `1px solid ${colors.borderColor}`, borderRadius: '8px', backgroundColor: 'transparent', color: colors.textPrimary, cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
          <button onClick={async () => { setLoading(true); try { await onConfirm(dia); } finally { setLoading(false); } }} disabled={loading} style={{ padding: '8px 16px', border: 'none', borderRadius: '8px', backgroundColor: colors.accentPrimary, color: 'white', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {loading && <Loader2 size={14} className="spin" />} <Send size={14} /> Reenviar dia {dia}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// JOB MODAL (progresso + resultado por cliente de uma ação em lote)
// ============================================================

const JobModal = ({ job: initialJob, onClose, colors }) => {
  const [job, setJob] = useState(initialJob);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!job || job.status === 'done' || job.status === 'error') return;
    const t = setInterval(async () => {
      try {
        const r = await leadService.jobStatus(job.id);
        setJob(r.data);
      } catch (e) {
        setErro(e?.response?.data?.detail || e.message);
      }
    }, 1500);
    return () => clearInterval(t);
  }, [job]);

  const done = job.status === 'done';
  const pct = job.total ? Math.round(((job.feitos || 0) / job.total) * 100) : 0;

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
      <div style={{ backgroundColor: colors.bgPrimary, borderRadius: '12px', padding: '22px', maxWidth: '640px', width: '92%', maxHeight: '84vh', display: 'flex', flexDirection: 'column', border: `1px solid ${colors.borderColor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            {job.acao_label}{job.dia ? ` (dia ${job.dia})` : ''}
          </h3>
          {done && (
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary }}><X size={20} /></button>
          )}
        </div>
        <p style={{ margin: '0 0 12px', fontSize: '13px', color: colors.textSecondary }}>
          {done
            ? `Concluído: ${job.ok || 0} com sucesso, ${job.falhas || 0} com falha, de ${job.total}.`
            : `Processando ${job.feitos || 0} de ${job.total}… (cada mensagem leva alguns segundos; pode deixar esta janela aberta)`}
        </p>
        <div style={{ height: '8px', borderRadius: '4px', backgroundColor: colors.bgSecondary, overflow: 'hidden', marginBottom: '14px' }}>
          <div style={{ width: `${pct}%`, height: '100%', backgroundColor: done ? (job.falhas ? '#f59e0b' : '#22c55e') : colors.accentPrimary, transition: 'width .4s' }} />
        </div>
        {erro && <div style={{ fontSize: '12px', color: '#ef4444', marginBottom: 8 }}>{erro}</div>}
        <div style={{ flex: 1, overflowY: 'auto', borderRadius: '8px', border: `1px solid ${colors.borderColor}` }}>
          {(job.resultados || []).length === 0 && (
            <div style={{ padding: '16px', fontSize: '13px', color: colors.textSecondary, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Loader2 size={14} className="spin" /> Iniciando…
            </div>
          )}
          {(job.resultados || []).map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '8px 12px', borderBottom: `1px solid ${colors.borderColor}`, fontSize: '13px' }}>
              {r.ok ? <CheckCircle2 size={16} color="#22c55e" style={{ flexShrink: 0, marginTop: 1 }} /> : <XCircle size={16} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />}
              <div style={{ flex: 1 }}>
                <div style={{ color: colors.textPrimary, fontWeight: 500 }}>{r.nome || r.lead_id} <span style={{ color: colors.textSecondary, fontWeight: 400, fontFamily: 'monospace', fontSize: 12 }}>{r.phone || ''}</span></div>
                <div style={{ color: r.ok ? colors.textSecondary : '#ef4444', fontSize: '12px' }}>{r.detalhe}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button onClick={onClose} disabled={!done} style={{ padding: '8px 16px', border: 'none', borderRadius: '8px', backgroundColor: done ? colors.accentPrimary : colors.bgSecondary, color: done ? 'white' : colors.textSecondary, cursor: done ? 'pointer' : 'wait', fontSize: '13px' }}>
            {done ? 'Fechar e atualizar' : 'Aguarde…'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// ACTION DROPDOWN (Portal-based to avoid table overflow clipping)
// ============================================================

const ActionDropdown = ({ lead, buttonRef, onClose, colors, onAcao, onDetalhes, onReenviarDia, onManageDays, onSendMessage, onEdit, onDelete }) => {
  const [pos, setPos] = useState({ top: 0, left: 0, openUp: false });

  useEffect(() => {
    if (!buttonRef?.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuHeight = 380;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight;
    setPos({ top: openUp ? rect.top - 4 : rect.bottom + 4, left: rect.right - 230, openUp });
  }, [buttonRef]);

  const p = lead.participante;
  const inscrito = !!p;
  const podeInscrever = !inscrito && !!lead.phone && !!lead.followup_id;

  const menuStyle = {
    position: 'fixed',
    top: pos.openUp ? undefined : `${pos.top}px`,
    bottom: pos.openUp ? `${window.innerHeight - pos.top}px` : undefined,
    left: `${Math.max(8, pos.left)}px`,
    backgroundColor: colors.bgPrimary, borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)', border: `1px solid ${colors.borderColor}`,
    zIndex: 9999, minWidth: '230px', padding: '4px',
  };
  const itemStyle = {
    display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
    padding: '8px 12px', border: 'none', background: 'transparent',
    color: colors.textPrimary, fontSize: '13px', cursor: 'pointer',
    borderRadius: '6px', textAlign: 'left',
  };
  const sep = <div style={{ height: '1px', backgroundColor: colors.borderColor, margin: '4px 0' }} />;
  const item = (icon, label, fn, extra = {}) => (
    <button onClick={() => { onClose(); fn(); }} style={{ ...itemStyle, ...extra }}>{icon} {label}</button>
  );

  return ReactDOM.createPortal(
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={onClose} />
      <div style={menuStyle}>
        {inscrito && item(<Eye size={14} />, 'Ver situação e histórico', () => onDetalhes(lead))}
        {inscrito && item(<Send size={14} />, 'Entregar pendentes agora', () => onAcao('entregar_pendentes', [lead]))}
        {inscrito && item(<RefreshCw size={14} />, 'Reenviar material de um dia…', () => onReenviarDia([lead]))}
        {inscrito && item(<Bell size={14} />, 'Enviar lembrete (pede SIM)', () => onAcao('lembrete', [lead]))}
        {podeInscrever && item(<UserPlus size={14} />, 'Inscrever no follow-up (dia 1)', () => onAcao('inscrever', [lead]), { color: '#22c55e' })}
        {!inscrito && !lead.phone && item(<Edit2 size={14} />, 'Cadastrar telefone para inscrever', () => onEdit(lead), { color: '#ef4444' })}
        {inscrito && item(<RotateCcw size={14} />, 'Reiniciar do dia 1', () => onAcao('reiniciar', [lead], `Reiniciar ${lead.name || lead.phone} do dia 1? O histórico dela neste follow-up é apagado.`))}
        {inscrito && item(<CalendarCheck size={14} />, 'Gerenciar dias recebidos', () => onManageDays(lead))}
        {sep}
        {item(<MessageSquare size={14} />, 'Enviar mensagem', () => onSendMessage(lead))}
        {item(<Edit2 size={14} />, 'Editar dados', () => onEdit(lead))}
        {sep}
        {inscrito && item(<UserMinus size={14} />, 'Remover do follow-up', () => onAcao('remover_followup', [lead], `Remover ${lead.name || lead.phone} do follow-up? Ela para de receber o desafio.`), { color: '#ef4444' })}
        {item(<Trash2 size={14} />, 'Remover lead', () => onDelete(lead), { color: '#ef4444' })}
      </div>
    </>,
    document.getElementById('modal-root') || document.body
  );
};


const ActionCell = ({ lead, openMenuId, setOpenMenuId, ...dropdownProps }) => {
  const btnRef = useRef(null);
  return (
    <td style={{ padding: '10px 14px', position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === lead.id ? null : lead.id); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: dropdownProps.colors.textSecondary, padding: '4px', borderRadius: '4px' }}
      >
        <MoreHorizontal size={18} />
      </button>
      {openMenuId === lead.id && (
        <ActionDropdown lead={lead} buttonRef={btnRef} onClose={() => setOpenMenuId(null)} {...dropdownProps} />
      )}
    </td>
  );
};


// ============================================================
// MAIN PAGE
// ============================================================

const FILTROS = [
  { key: 'todos', label: 'Todos', icon: Users, color: '#6366f1', count: c => c.total || 0, hint: 'Todos os leads' },
  { key: 'atencao', label: 'Precisam de atenção', icon: Flag, color: '#ef4444', count: c => c.atencao || 0, hint: 'Parados, com falha, reenvio pendente, não inscritos ou sem telefone' },
  { key: 'em_espera', label: 'Parados', icon: PauseCircle, color: '#f59e0b', count: c => c.em_espera || 0, hint: 'Não responderam: o material do dia está segurado até responderem' },
  { key: 'falha', label: 'Falha de envio', icon: XCircle, color: '#ef4444', count: c => c.falha || 0, hint: 'Material que falhou e esgotou as tentativas' },
  { key: 'pendente', label: 'Reenvio pendente', icon: Clock, color: '#3b82f6', count: c => c.pendente || 0, hint: 'O sistema ainda vai tentar sozinho (07h-21h)' },
  { key: 'nao_inscrito', label: 'Não inscritos', icon: UserPlus, color: '#ef4444', count: c => (c.nao_inscrito || 0) + (c.sem_telefone || 0), hint: 'Pagaram mas não estão no follow-up (inclui sem telefone)' },
  { key: 'em_dia', label: 'Em dia', icon: CheckCircle2, color: '#22c55e', count: c => c.em_dia || 0, hint: 'Tudo entregue até hoje' },
  { key: 'concluido', label: 'Concluídos', icon: Check, color: '#64748b', count: c => c.concluido || 0, hint: 'Terminaram o desafio' },
];

// Compras não concluídas (21/10/2026): preencheu nome/WhatsApp no checkout e não pagou.
// O lead só é criado no pagamento — sem este painel a equipe não via essas pessoas.
const ComprasNaoConcluidas = ({ pedidos, colors }) => {
  const [aberto, setAberto] = useState(false);
  if (!pedidos.length) return null;
  const fmtData = (iso) => { try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };
  const fmtTel = (p) => (p || '').replace(/^55(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3');
  return (
    <div style={{ border: `1px solid ${colors.borderColor}`, borderRadius: 10, backgroundColor: colors.bgSecondary }}>
      <button onClick={() => setAberto(a => !a)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', color: colors.textPrimary, fontSize: 13, fontWeight: 600 }}>
        <ShoppingCart size={16} color="#f59e0b" />
        Compras não concluídas ({pedidos.length})
        <span style={{ fontWeight: 400, color: colors.textSecondary }}>— preencheram os dados no checkout e não pagaram</span>
        <span style={{ marginLeft: 'auto', display: 'flex' }}>{aberto ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
      </button>
      {aberto && (
        <div style={{ overflowX: 'auto', borderTop: `1px solid ${colors.borderColor}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: colors.textSecondary, textAlign: 'left' }}>
                {['Quando', 'Nome', 'WhatsApp', 'E-mail', 'Produto', 'Valor', ''].map(h => <th key={h} style={{ padding: '8px 14px', fontWeight: 600 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {pedidos.map(p => (
                <tr key={p.id} style={{ borderTop: `1px solid ${colors.borderColor}`, color: colors.textPrimary }}>
                  <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>{fmtData(p.created_at)}</td>
                  <td style={{ padding: '8px 14px' }}>{p.name}</td>
                  <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>{fmtTel(p.phone)}</td>
                  <td style={{ padding: '8px 14px' }}>{p.email || '—'}</td>
                  <td style={{ padding: '8px 14px' }}>{p.product_name}</td>
                  <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>R$ {Number(p.amount || 0).toFixed(2).replace('.', ',')}</td>
                  <td style={{ padding: '8px 14px' }}>
                    <a href={`https://wa.me/${p.phone}`} target="_blank" rel="noopener noreferrer" style={{ color: colors.accentPrimary, display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      <MessageSquare size={13} /> Chamar
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const LeadsPage = () => {
  const { colors } = useTheme();

  // Data
  const [painel, setPainel] = useState({ leads: [], contagem: {}, followups: {} });
  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [pedidosPendentes, setPedidosPendentes] = useState([]);

  // Filters
  const [filtro, setFiltro] = useState('atencao');
  const [pagamentoFiltro, setPagamentoFiltro] = useState('');
  const [produtoFiltro, setProdutoFiltro] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Selection
  const [selecionados, setSelecionados] = useState(new Set());

  // Modals
  const [editingLead, setEditingLead] = useState(null);
  const [sendMessageLead, setSendMessageLead] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [manageDaysLead, setManageDaysLead] = useState(null);
  const [reenviarLeads, setReenviarLeads] = useState(null);
  const [job, setJob] = useState(null);
  const [detalheLead, setDetalheLead] = useState(null);
  const [detalheHistory, setDetalheHistory] = useState([]);

  // Action menu
  const [openMenuId, setOpenMenuId] = useState(null);

  const loadData = useCallback(async () => {
    setErroCarga('');
    try {
      const res = await leadService.painel();
      setPainel(res.data || { leads: [], contagem: {}, followups: {} });
      productService.listPendingOrders().then(r => setPedidosPendentes(r.data || [])).catch(() => {});
    } catch (err) {
      console.error('Error loading leads panel:', err);
      setErroCarga(err?.response?.data?.detail || err.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const leads = useMemo(() => painel.leads || [], [painel.leads]);
  const contagem = painel.contagem || {};

  // depois de qualquer mudança, os selecionados que sumiram do painel saem da seleção
  useEffect(() => {
    setSelecionados(prev => {
      const ids = new Set(leads.map(l => l.id));
      const next = new Set([...prev].filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [leads]);

  const produtos = useMemo(() => {
    const m = new Map();
    leads.forEach(l => { if (l.product_name) m.set(l.payment_link_id || l.product_name, l.product_name); });
    return [...m.entries()];
  }, [leads]);

  const filteredLeads = useMemo(() => {
    let result = leads;
    if (filtro === 'atencao') result = result.filter(l => l.precisa_atencao);
    else if (filtro === 'nao_inscrito') result = result.filter(l => l.situacao === 'nao_inscrito' || l.situacao === 'sem_telefone');
    else if (filtro !== 'todos') result = result.filter(l => l.situacao === filtro);
    if (pagamentoFiltro) result = result.filter(l => l.plan_status === pagamentoFiltro);
    if (produtoFiltro) result = result.filter(l => (l.payment_link_id || l.product_name) === produtoFiltro);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l => (l.name || '').toLowerCase().includes(q) || (l.phone || '').includes(q) || (l.email || '').toLowerCase().includes(q));
    }
    return result;
  }, [leads, filtro, pagamentoFiltro, produtoFiltro, searchQuery]);

  const totalDays = useMemo(() => Math.max(0, ...Object.values(painel.followups || {}).map(f => f.total_days || 0)), [painel.followups]);

  // ---- ações
  const dispararAcao = async (acao, leadsAlvo, confirmText = null, dia = null) => {
    if (confirmText && !window.confirm(confirmText)) return;
    const ids = leadsAlvo.map(l => l.id);
    if (!ids.length) return;
    try {
      const r = await leadService.acaoLote(acao, ids, dia);
      setJob(r.data);
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || err.message));
    }
  };

  const fecharJob = async () => { setJob(null); setSelecionados(new Set()); await loadData(); };

  const handleReenviarDia = async (dia) => {
    const alvo = reenviarLeads || [];
    setReenviarLeads(null);
    await dispararAcao('reenviar_dia', alvo, null, dia);
  };

  const abrirDetalhes = async (lead) => {
    setDetalheLead(lead);
    setDetalheHistory([]);
    if (!lead.followup_id || !lead.phone) return;
    try {
      const r = await followupService.getHistory(lead.followup_id, null, lead.phone);
      setDetalheHistory(r.data || []);
    } catch (e) { console.error(e); }
  };

  const handleUpdateLead = async (leadId, data) => { await leadService.update(leadId, data); await loadData(); };
  const handleCreateLead = async (_, data) => { await leadService.create(data); await loadData(); };
  const handleSendMessage = async (leadId, message) => { await leadService.sendMessage(leadId, message); };
  const handleDeleteLead = async (leadId) => {
    setActionLoading(true);
    try { await leadService.delete(leadId); setConfirmAction(null); await loadData(); }
    catch (err) { console.error('Error deleting lead:', err); }
    finally { setActionLoading(false); }
  };

  // ---- seleção
  const toggleSel = (id) => setSelecionados(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const todosFiltradosSel = filteredLeads.length > 0 && filteredLeads.every(l => selecionados.has(l.id));
  const toggleTodos = () => setSelecionados(todosFiltradosSel ? new Set() : new Set(filteredLeads.map(l => l.id)));
  const leadsSel = leads.filter(l => selecionados.has(l.id));
  const selInscritos = leadsSel.filter(l => l.participante);
  const selInscreviveis = leadsSel.filter(l => !l.participante && l.phone && l.followup_id);

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
        <Loader2 size={32} color={colors.accentPrimary} className="spin" />
        <span style={{ fontSize: '14px', color: colors.textSecondary }}>Carregando clientes e situação no desafio...</span>
      </div>
    );
  }

  const pagamentoFiltros = [
    { value: '', label: 'Qualquer pagamento' },
    { value: 'active', label: 'Pagos' },
    { value: 'pending', label: 'Pagamento pendente' },
    { value: 'overdue', label: 'Não concluído' },
    { value: 'refunded', label: 'Reembolsados' },
  ];

  const bulkBtn = (extra = {}) => ({
    display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer', border: `1px solid ${colors.borderColor}`,
    backgroundColor: colors.bgPrimary, color: colors.textPrimary, ...extra,
  });

  const th = (h) => (
    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${colors.borderColor}` }}>{h}</th>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: colors.bgPrimary, overflow: 'auto' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>Leads</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: colors.textSecondary }}>
            Quem pagou, em que dia está e o que precisa de manutenção. Clique num cartão para filtrar.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={loadData} title="Atualizar" style={bulkBtn()}><RefreshCw size={14} /> Atualizar</button>
          <button onClick={() => setEditingLead({})} style={{ ...bulkBtn(), backgroundColor: colors.accentPrimary, color: 'white', border: 'none' }}>
            <Plus size={16} /> Novo Lead
          </button>
        </div>
      </div>

      {erroCarga && (
        <div style={{ margin: '12px 24px 0', padding: '10px 14px', borderRadius: 8, backgroundColor: '#ef444415', color: '#ef4444', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertCircle size={16} /> {erroCarga}
        </div>
      )}

      <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

        <ComprasNaoConcluidas pedidos={pedidosPendentes} colors={colors} />

        {/* Cartões-filtro */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {FILTROS.map(f => (
            <FiltroCard key={f.key} label={f.label} value={f.count(contagem)} icon={f.icon} color={f.color}
              active={filtro === f.key} onClick={() => setFiltro(f.key)} colors={colors} hint={f.hint} />
          ))}
        </div>

        {/* Filtros secundários */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={pagamentoFiltro} onChange={e => setPagamentoFiltro(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary, color: colors.textPrimary, fontSize: 12 }}>
            {pagamentoFiltros.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {produtos.length > 1 && (
            <select value={produtoFiltro} onChange={e => setProdutoFiltro(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary, color: colors.textPrimary, fontSize: 12 }}>
              <option value="">Todos os produtos</option>
              {produtos.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
            </select>
          )}
          <span style={{ fontSize: 12, color: colors.textSecondary }}>{filteredLeads.length} de {leads.length}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto', padding: '6px 12px', borderRadius: '8px', border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary, minWidth: '220px' }}>
            <Search size={14} color={colors.textSecondary} />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar nome, telefone ou e-mail..."
              style={{ border: 'none', outline: 'none', backgroundColor: 'transparent', color: colors.textPrimary, fontSize: '13px', flex: 1 }} />
          </div>
        </div>

        {/* Barra de ações em lote */}
        {selecionados.size > 0 && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', borderRadius: 10, backgroundColor: `${colors.accentPrimary}12`, border: `1px solid ${colors.accentPrimary}40` }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>{selecionados.size} selecionado(s)</span>
            {selInscritos.length > 0 && <>
              <button style={bulkBtn()} onClick={() => dispararAcao('entregar_pendentes', selInscritos)}><Send size={13} /> Entregar pendentes ({selInscritos.length})</button>
              <button style={bulkBtn()} onClick={() => setReenviarLeads(selInscritos)}><RefreshCw size={13} /> Reenviar dia… ({selInscritos.length})</button>
              <button style={bulkBtn()} onClick={() => dispararAcao('lembrete', selInscritos, `Enviar o lembrete de abertura (template) para ${selInscritos.length} cliente(s)?`)}><Bell size={13} /> Enviar lembrete ({selInscritos.length})</button>
            </>}
            {selInscreviveis.length > 0 && (
              <button style={bulkBtn({ color: '#22c55e' })} onClick={() => dispararAcao('inscrever', selInscreviveis, `Inscrever ${selInscreviveis.length} cliente(s) no follow-up (começam no dia 1)?`)}><UserPlus size={13} /> Inscrever ({selInscreviveis.length})</button>
            )}
            <button style={{ ...bulkBtn(), marginLeft: 'auto', border: 'none', backgroundColor: 'transparent', color: colors.textSecondary }} onClick={() => setSelecionados(new Set())}><X size={13} /> Limpar</button>
          </div>
        )}

        {/* Tabela */}
        {filteredLeads.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary, fontSize: '14px' }}>
            {leads.length === 0 ? 'Nenhum lead cadastrado ainda.' : (filtro === 'atencao' ? 'Ninguém precisa de atenção agora. 🎉' : 'Nenhum lead encontrado com os filtros atuais.')}
          </div>
        ) : (
          <div style={{ borderRadius: '12px', border: `1px solid ${colors.borderColor}`, overflow: 'visible' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: colors.bgSecondary }}>
                  <th style={{ padding: '10px 12px', width: 34, borderBottom: `1px solid ${colors.borderColor}` }}>
                    <button onClick={toggleTodos} title="Selecionar todos os filtrados" style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary, padding: 0, display: 'flex' }}>
                      {todosFiltradosSel ? <CheckSquare size={16} color={colors.accentPrimary} /> : <Square size={16} />}
                    </button>
                  </th>
                  {['Nome', 'Telefone', 'Produto', 'Dia', 'Situação', 'Alertas', 'Pagamento', 'Ações'].map(th)}
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map(lead => {
                  const p = lead.participante;
                  const sel = selecionados.has(lead.id);
                  return (
                    <tr key={lead.id} style={{ borderBottom: `1px solid ${colors.borderColor}`, backgroundColor: sel ? `${colors.accentPrimary}08` : (lead.precisa_atencao ? '#ef44440a' : 'transparent') }}>
                      <td style={{ padding: '10px 12px' }}>
                        <button onClick={() => toggleSel(lead.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary, padding: 0, display: 'flex' }}>
                          {sel ? <CheckSquare size={16} color={colors.accentPrimary} /> : <Square size={16} />}
                        </button>
                      </td>
                      <td style={{ padding: '10px 12px', color: colors.textPrimary, fontWeight: 500 }}>
                        {lead.name || '-'}
                        {lead.email && <div style={{ fontSize: 11, color: colors.textSecondary, fontWeight: 400 }}>{lead.email}</div>}
                      </td>
                      <td style={{ padding: '10px 12px', color: colors.textSecondary, fontFamily: 'monospace', fontSize: 12 }}>{lead.phone || <span style={{ color: '#ef4444' }}>sem telefone</span>}</td>
                      <td style={{ padding: '10px 12px', color: colors.textSecondary }}>
                        {lead.product_name || '-'}
                        {lead.provider && <div style={{ fontSize: 10, color: colors.textSecondary, textTransform: 'uppercase' }}>{lead.provider}</div>}
                      </td>
                      <td style={{ padding: '10px 12px', color: colors.textPrimary, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {p ? `${p.dia_atual ?? '-'} / ${p.total_days || '?'}` : '-'}
                        {p?.dias_segurados > 0 && <div style={{ fontSize: 10, color: colors.textSecondary, fontWeight: 400 }}>{p.dias_segurados} dia(s) segurado(s)</div>}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <SituacaoBadge situacao={lead.situacao} colors={colors} />
                        {p?.status === 'em_espera' && <div style={{ fontSize: 10, color: colors.textSecondary, marginTop: 3 }}>parado no dia {p.dia_parado} há {p.espera_dias} dia(s)</div>}
                      </td>
                      <td style={{ padding: '10px 12px', maxWidth: 280 }}><AlertasCell lead={lead} colors={colors} /></td>
                      <td style={{ padding: '10px 12px' }}><StatusBadge status={lead.plan_status} colors={colors} /></td>
                      <ActionCell
                        lead={lead}
                        openMenuId={openMenuId}
                        setOpenMenuId={setOpenMenuId}
                        colors={colors}
                        onAcao={(acao, alvo, confirmText) => dispararAcao(acao, alvo, confirmText)}
                        onDetalhes={abrirDetalhes}
                        onReenviarDia={(alvo) => setReenviarLeads(alvo)}
                        onManageDays={(l) => setManageDaysLead(l)}
                        onSendMessage={(l) => setSendMessageLead(l)}
                        onEdit={(l) => setEditingLead(l)}
                        onDelete={(l) => setConfirmAction({ type: 'delete', lead: l })}
                      />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ============================================ MODALS ============================================ */}

      {editingLead !== null && (
        <EditLeadModal lead={editingLead.id ? editingLead : null} onClose={() => setEditingLead(null)} onSave={editingLead.id ? handleUpdateLead : handleCreateLead} colors={colors} />
      )}

      {manageDaysLead && (
        <FollowupDaysModal lead={manageDaysLead} onClose={() => setManageDaysLead(null)} onSaved={loadData} colors={colors} />
      )}

      {sendMessageLead && (
        <SendMessageModal lead={sendMessageLead} onClose={() => setSendMessageLead(null)} onSend={handleSendMessage} colors={colors} />
      )}

      {reenviarLeads && (
        <ReenviarDiaModal leads={reenviarLeads} totalDays={totalDays} onConfirm={handleReenviarDia} onClose={() => setReenviarLeads(null)} colors={colors} />
      )}

      {job && <JobModal job={job} onClose={fecharJob} colors={colors} />}

      {detalheLead && (
        <RecipientDetailModal
          isOpen={true}
          phone={detalheLead.phone}
          history={detalheHistory}
          onClose={() => setDetalheLead(null)}
          colors={colors}
          followupId={detalheLead.followup_id}
          totalDays={detalheLead.participante?.total_days}
          situacao={detalheLead.participante}
          onChanged={async () => { await loadData(); await abrirDetalhes(detalheLead); }}
          onRemove={async (phone) => {
            try { await followupService.removeRecipient(detalheLead.followup_id, phone); } catch (e) { alert('Erro: ' + (e.response?.data?.detail || e.message)); }
            setDetalheLead(null); await loadData();
          }}
          onResend={async (day) => {
            if (!window.confirm(`Reenviar agora todo o conteúdo do Dia ${day} para ${detalheLead.phone}?\nO dia da pessoa no desafio não muda.`)) return;
            try {
              const res = await followupService.sendDay(detalheLead.followup_id, day, [detalheLead.phone], false, true);
              const d = res.data || {};
              alert(`Dia ${day}: ${(d.sent || []).length} mensagem(ns) enviada(s), ${(d.failed || []).length} com erro, ${(d.skipped || []).length} pulada(s).`);
              await abrirDetalhes(detalheLead); await loadData();
            } catch (err) { alert('Erro ao reenviar: ' + (err.response?.data?.detail || err.message)); }
          }}
        />
      )}

      {confirmAction?.type === 'delete' && (
        <ConfirmModal
          title="Remover lead"
          message={`Tem certeza que deseja remover "${confirmAction.lead.name}"? Esta acao nao pode ser desfeita.`}
          onConfirm={() => handleDeleteLead(confirmAction.lead.id)}
          onCancel={() => setConfirmAction(null)}
          loading={actionLoading}
          colors={colors}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default LeadsPage;
